import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { Modal } from "../../components/ui/Modal";
import type { PersonalItem, PersonalInventoryEntry } from "../../types";

interface Props {
  existing: PersonalInventoryEntry[];
  onClose: () => void;
  onSaved: () => void;
}

/** Gegenstand aus dem Katalog auswählen und mit Startmenge zum eigenen Bestand hinzufügen. */
export function AddItemModal({ existing, onClose, onSaved }: Props) {
  const [items, setItems] = useState<PersonalItem[]>([]);
  const [itemId, setItemId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get<PersonalItem[]>("/api/personal-inventory/items").then(setItems).catch(() => {});
  }, []);

  const existingIds = new Set(existing.map((e) => e.item_id));
  const selectable = items.filter((i) => !existingIds.has(i.id));

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/personal-inventory/mine", { itemId: Number(itemId), quantity: Number(quantity) });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Hinzufügen fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Gegenstand hinzufügen"
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn-primary" form="add-item-form" disabled={submitting || !itemId}>Hinzufügen</button>
        </>
      }
    >
      <form id="add-item-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div>
          <label className="label">Gegenstand *</label>
          <select className="input" value={itemId} onChange={(e) => setItemId(e.target.value)} required>
            <option value="">— auswählen —</option>
            {selectable.map((i) => (
              <option key={i.id} value={i.id}>{i.name}{i.category_name ? ` (${i.category_name})` : ""}</option>
            ))}
          </select>
          {selectable.length === 0 && items.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">Alle verfügbaren Gegenstände sind bereits in Ihrem Bestand.</p>
          )}
        </div>
        <div>
          <label className="label">Menge *</label>
          <input className="input" type="number" min={0} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} required />
        </div>
      </form>
    </Modal>
  );
}
